<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="referencesPendingScreeningConnection"
    :pending-connection-query="
      $apollo.queries.referencesPendingScreeningConnection
    "
    :history-connection="referencesByVoteHistoryConnection"
    :history-connection-query="
      $apollo.queries.referencesByVoteHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeVote"
    :get-vote-by-current-user-func="getVoteByCurrentUser"
    :filtering-input="filteringInput"
    restrict-to-voting-only
    @selected-reference-change="onSelectedReferenceChange"
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <screen-tab-pending-empty-state />
    </template>
  </screening-workflow-panel>
</template>

<script>
import ScreeningWorkflowPanel from './ScreeningWorkflowPanel';
import ScreenTabPendingEmptyState from './ScreenTabPendingEmptyState';
import {
  GetReferencesPendingScreening,
  GetReferencesByScreeningVoteHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ScreenTabPending',
  components: {
    ScreeningWorkflowPanel,
    ScreenTabPendingEmptyState,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    hasOpenedReference: false,
    referencesPendingScreeningConnection: null,
    referencesByVoteHistoryConnection: null,
  }),
  apollo: {
    referencesPendingScreeningConnection: {
      query: GetReferencesPendingScreening,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesPendingScreening
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByVoteHistoryConnection: {
      query: GetReferencesByScreeningVoteHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByScreeningVoteHistory
        ),
      skip() {
        return !this.review?.id || !this.hasOpenedReference;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (active && this.referencesPendingScreeningConnection) {
        this.$apollo.queries.referencesPendingScreeningConnection.refetch();
        if (this.hasOpenedReference) {
          this.$apollo.queries.referencesByVoteHistoryConnection.refetch();
        }
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesPendingScreeningConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeVote: 'reviewerScreeningVote/deleteWithCacheUpdate',
    }),
    onSelectedReferenceChange(reference) {
      if (reference && !this.hasOpenedReference) {
        this.hasOpenedReference = true;
      }
    },
    getVoteByCurrentUser(reference) {
      return reference?.getScreeningVoteBy(this.currentResearcher);
    },
  },
};
</script>
